import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { getDiscoveryRedirectUrl } from '@/app/sign-in/discoveryRedirect';
import RateLimit from '@/app/sign-in/RateLimitButton';
import SignInPanel from '@/app/sign-in/SignInPanel';
import { businessEmail, useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { maxEmailLength } from '@/constants/numbers';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import { setPendingSignInMethod } from '@/helper/user-preferences';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

const SignUpEmail = () => {
  const [searchParams] = useSearchParams();
  const stytch = useStytchB2BClient();
  const { pushNotification, removeAllNotifications } = useNotification();
  const redirect = searchParams.get('redirect');
  const formSchema = z.object({
    email: businessEmail(),
  });

  const discoveryRedirectUrl = getDiscoveryRedirectUrl(true, redirect);

  const form = useAppForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value: { email } }) => {
      setPendingSignInMethod('email');

      try {
        await stytch.magicLinks.email.discovery.send({
          email_address: email,
          discovery_redirect_url: discoveryRedirectUrl,
          login_template_id: 'c99_discovery_signup',
        });

        removeAllNotifications();
        pushNotification({
          type: 'success',
          message: (
            <Trans>
              A one-time access link was sent to{' '}
              <Text variant="caption1" weight="extra-bold">
                {email}
              </Text>
              . You'll be able to log in and change your password after verification. If you don't
              receive an email, reach out to support@channel99.com or try again.
            </Trans>
          ),
        });
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signUpEmail.path),
        });
      }
    },
  });

  return (
    <FullPage title={t`Sign Up with Email`}>
      <SignInPanel
        title={t`Sign Up with Email`}
        description={
          <Trans>
            We'll send a one-time access link to your business email to finish creating your
            account.
          </Trans>
        }
        footer={
          <>
            <Text variant="caption1" color="grey">
              <Trans>
                Prefer to not use email?{' '}
                <Link
                  variant="caption1"
                  color="aqua"
                  to={`${ROUTES.signUp.path}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`}
                >
                  Sign up another way
                </Link>
              </Trans>
            </Text>
          </>
        }
        showGap
      >
        <form
          className="flex flex-col gap-7"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField name="email">
            {(field) => (
              <field.Field>
                <FieldLabel htmlFor="sign-up-email">
                  <Trans>Business email</Trans>
                </FieldLabel>
                <field.TextInput
                  id="sign-up-email"
                  inputMode="email"
                  placeholder={t`name@work-email.com`}
                  autoComplete="email"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoFocus
                  maxLength={maxEmailLength}
                  size="large"
                />
                <field.Error />
              </field.Field>
            )}
          </form.AppField>
          <form.AppForm>
            <RateLimit>
              <form.SubmitButton variant="primary" size="large" block>
                <Trans>Email me a link</Trans>
              </form.SubmitButton>
            </RateLimit>
          </form.AppForm>
        </form>
      </SignInPanel>
    </FullPage>
  );
};

export default SignUpEmail;
